import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Shield, ArrowLeft, Scale, FileText, Lock, AlertTriangle, Cpu } from "lucide-react";

const SECTIONS = [
  {
    id: "mentions",
    title: "Mentions légales",
    icon: Scale,
    content: [
      { subtitle: "Éditeur", text: "3H33 SARL au capital de 33 000 € — Conseil, formation et intégration en IA\n24 rue Vauthier, 92100 Boulogne-Billancourt\nSIRET : 503 639 247 00317\nRCS Nanterre : B 503 639 247\nTVA : FR66503639247\nDirecteur de la publication : Alexandre\nContact : info@3h33.fr" },
      { subtitle: "Hébergement", text: "Hostinger International Ltd — 61 Lordou Vironos Street, 6023 Larnaca, Chypre\nhttps://www.hostinger.fr\nL'application utilise des API d'intelligence artificielle pour la génération de réponses." },
      { subtitle: "Nature", text: "Outil pédagogique et informatif. Ne constitue pas un conseil juridique au sens de la loi n°71-1130." },
      { subtitle: "Responsabilité", text: "3H33 ne saurait être responsable de décisions prises sur la base de cet outil." },
      { subtitle: "Médiation", text: "Conformément aux art. L.616-1 et R.616-1 du Code de la consommation.\nMédiateur : MEDICYS, 73 bd de Clichy, 75009 Paris — www.medicys.fr\nRéclamation préalable : info@3h33.fr" },
    ]
  },
  {
    id: "cgu",
    title: "Conditions Générales d'Utilisation",
    icon: FileText,
    content: [
      { subtitle: "Objet", text: "Les présentes conditions régissent l'utilisation de l'application IA Conforme, éditée par 3H33 SARL." },
      { subtitle: "Accès", text: "L'accès est libre (freemium) ou payant (premium). L'utilisation doit se faire de bonne foi, à des fins informatives et pédagogiques." },
      { subtitle: "Limitations", text: "• Les réponses IA sont fournies à titre indicatif\n• Elles ne se substituent pas à un conseil juridique\n• Des imprécisions sont possibles, liées à l'évolution réglementaire\n• Les informations ne sont pas garanties exhaustives" },
      { subtitle: "Propriété intellectuelle", text: "Le contenu est la propriété de 3H33 SARL. Toute reproduction est interdite sans autorisation préalable." },
      { subtitle: "Modification", text: "3H33 se réserve le droit de modifier ces conditions. Les utilisateurs seront informés des changements substantiels." },
    ]
  },
  {
    id: "confidentialite",
    title: "Politique de confidentialité",
    icon: Lock,
    content: [
      { subtitle: "Responsable du traitement", text: "3H33 SARL — 24 rue Vauthier, 92100 Boulogne-Billancourt\nSIRET : 503 639 247 00317 — Contact : info@3h33.fr" },
      { subtitle: "Données collectées", text: "• Secteur d'activité et taille de la structure (onboarding)\n• Contexte libre (facultatif)\n• Messages échangés avec l'IA\n• Adresse email et nom (inscription)" },
      { subtitle: "Traitement", text: "• Les données sont transmises à l'API d'IA pour générer les réponses\n• Les conversations sont stockées de manière sécurisée\n• Les données de paiement sont traitées par Stripe (PCI-DSS compliant)" },
      { subtitle: "Base légale", text: "Consentement (art. 6.1.a RGPD) par utilisation volontaire du service." },
      { subtitle: "Transfert hors UE", text: "Les serveurs d'IA peuvent être situés hors UE. Les transferts sont encadrés par des clauses contractuelles types." },
      { subtitle: "Vos droits", text: "Accès, rectification, effacement, portabilité, limitation, opposition : info@3h33.fr" },
      { subtitle: "Cookies", text: "L'application utilise uniquement des cookies techniques d'authentification (httpOnly, sécurisés). Aucun cookie publicitaire ni traceur." },
      { subtitle: "DPO", text: "Contact : info@3h33.fr" },
    ]
  },
  {
    id: "avertissement",
    title: "Avertissement",
    icon: AlertTriangle,
    content: [
      { subtitle: "Ce que cet outil est", text: "• Un outil de vulgarisation et de sensibilisation\n• Un assistant pédagogique sur la réglementation IA\n• Un point de départ pour approfondir avec un professionnel" },
      { subtitle: "Ce qu'il n'est pas", text: "• Un cabinet d'avocats\n• Un outil certifié CNIL\n• Un substitut au DPO ou au juriste\n• Une source faisant foi en cas de litige" },
      { subtitle: "Précautions", text: "• L'IA peut contenir des erreurs ou des imprécisions\n• La réglementation évolue constamment\n• Chaque situation est unique et nécessite une analyse spécifique\n• En cas de doute, consultez un professionnel du droit" },
    ]
  },
  {
    id: "conformite-ia",
    title: "Conformité IA",
    icon: Cpu,
    content: [
      { subtitle: "Classification : Risque Minimal", text: "Outil d'assistance informationnelle. Aucune décision automatisée affectant les droits des personnes." },
      { subtitle: "Transparence respectée", text: "• Information claire de l'interaction avec une IA\n• Éditeur identifié (3H33 SARL)\n• Modèle IA identifié et configurable\n• Avertissement permanent sur la nature non juridique des réponses" },
      { subtitle: "Bonnes pratiques appliquées", text: "• Prompt système encadré avec des limites claires\n• Rappel permanent : ne remplace pas un avis juridique\n• Pas de profilage ni de décision automatisée\n• Pas de collecte de données sensibles (art. 9 RGPD)\n• Onboarding volontaire et facultatif" },
      { subtitle: "Sous-traitants IA", text: "Les modèles d'IA utilisés sont fournis par des tiers (Anthropic, Google, OpenAI, etc.). Les données transmises via les API ne sont pas utilisées pour l'entraînement des modèles." },
    ]
  },
];

export default function LegalPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const initialSection = searchParams.get("section") || "mentions";
  const [activeSection, setActiveSection] = useState(initialSection);

  const section = SECTIONS.find(s => s.id === activeSection) || SECTIONS[0];

  return (
    <div className="min-h-screen bg-[var(--ia-surface)]" data-testid="legal-page">
      {/* Header */}
      <header className="bg-white border-b border-[var(--ia-border)] sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 h-14 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate(-1)} data-testid="legal-back-btn">
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <Shield className="h-6 w-6 text-[var(--ia-navy)]" />
            <span className="text-lg font-bold text-[var(--ia-navy)]" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>IA Conforme</span>
          </div>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
        <h1 className="text-2xl sm:text-3xl font-bold text-[var(--ia-text)] mb-6" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
          Informations légales
        </h1>

        {/* Navigation tabs */}
        <div className="flex gap-2 mb-8 overflow-x-auto pb-2 flex-wrap">
          {SECTIONS.map(s => (
            <button key={s.id} onClick={() => setActiveSection(s.id)} data-testid={`legal-tab-${s.id}`}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-md text-sm font-medium whitespace-nowrap transition-all ${activeSection === s.id ? "bg-[var(--ia-navy)] text-white" : "bg-white border border-[var(--ia-border)] text-slate-600 hover:bg-slate-50"}`}>
              <s.icon className="h-3.5 w-3.5" />
              {s.title}
            </button>
          ))}
        </div>

        {/* Content */}
        <Card className="border-[var(--ia-border)] animate-fade-in" key={activeSection}>
          <CardContent className="p-6 sm:p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-md bg-[var(--ia-navy)]/5 flex items-center justify-center">
                <section.icon className="h-5 w-5 text-[var(--ia-navy)]" />
              </div>
              <h2 className="text-xl font-bold text-[var(--ia-text)]" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                {section.title}
              </h2>
            </div>

            <div className="space-y-6">
              {section.content.map((block, i) => (
                <div key={i}>
                  {i > 0 && <Separator className="mb-6" />}
                  <h3 className="text-sm font-semibold text-[var(--ia-navy)] mb-2">{block.subtitle}</h3>
                  <div className="text-sm text-slate-600 leading-relaxed whitespace-pre-line">
                    {block.text}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Footer */}
        <div className="mt-8 text-center text-xs text-[var(--ia-muted)]">
          <p>© 3H33 SARL — Outil pédagogique, ne remplace pas un avis juridique</p>
          <p className="mt-1">24 rue Vauthier, 92100 Boulogne-Billancourt — info@3h33.fr</p>
        </div>
      </div>
    </div>
  );
}
